import type { RawDocumentContent } from '@packages/types';
import {
    createApi,
    fetchBaseQuery,
    type BaseQueryFn,
    type FetchArgs,
    type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { getCsrfToken, refreshCsrfToken } from '@ui/utils/csrf';
import { logger } from '@ui/utils/logger';
import { gzipFetch } from '@ui/utils/gzipFetch';

/**
 * Base query with CSRF token handling and automatic retry on CSRF failures
 */
const baseQueryWithCsrf: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions
) => {
    const baseQuery = fetchBaseQuery({
        baseUrl: '/api',
        credentials: 'include', // Required for CSRF cookies
        fetchFn: gzipFetch, // Auto-compress large request bodies
        paramsSerializer: params => {
            const searchParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (value === undefined || value === null) return;
                if (Array.isArray(value)) {
                    value.forEach(v => searchParams.append(key, String(v)));
                } else {
                    searchParams.append(key, String(value));
                }
            });
            return searchParams.toString();
        },
        prepareHeaders: headers => {
            // Add CSRF token to all requests
            // Server only validates CSRF for POST, PUT, PATCH, DELETE (not GET, HEAD, OPTIONS)
            const csrfToken = getCsrfToken();

            if (csrfToken) {
                headers.set('x-csrf-token', csrfToken);
            }

            return headers;
        },
    });

    // First attempt
    let result = await baseQuery(args, api, extraOptions);

    // If we get a 403 (Forbidden) error, it might be due to invalid/expired CSRF token
    // Refresh the token and retry once
    if (result.error && result.error.status === 403) {
        logger.warn('CSRF token may be invalid, refreshing and retrying...');

        try {
            // Refresh CSRF token
            await refreshCsrfToken();

            // Retry the request with new token
            result = await baseQuery(args, api, extraOptions);
        } catch (error) {
            logger.error('Failed to refresh CSRF token', error);
        }
    }

    if (result.error && result.error.status === 401) {
        const currentPath = window.location.pathname + window.location.search;
        const inProgress = sessionStorage.getItem('api_auth_redirect_in_progress') === 'true';
        const lastTs = parseInt(sessionStorage.getItem('api_auth_last_redirect_ts') || '0', 10);
        const now = Date.now();
        const url = typeof args === 'string' ? args : args.url;
        const method = typeof args === 'string' ? 'GET' : args.method || 'GET';
        logger.warn('[API 401]', { url, method, path: currentPath, inProgress, lastTs });
        if (!inProgress && !window.location.pathname.includes('/auth/login') && now - lastTs > 5000) {
            sessionStorage.setItem('api_auth_redirect_in_progress', 'true');
            sessionStorage.setItem('api_auth_last_redirect_ts', String(now));
            window.location.href = `/api/auth/login?redirect=${encodeURIComponent(currentPath)}`;
        }
    }

    return result;
};

export const api = createApi({
    reducerPath: 'api',
    baseQuery: baseQueryWithCsrf,
    tagTypes: [
        'Audit',
        'AuditStats',
        'Cohorts',
        'Curations',
        'Dashboard',
        'Documents',
        'DocumentBrowse',
        'DocumentContent',
        'MetricDocuments',
        'Patients',
        'PatientEvents',
        'Ontologies',
        'Ontology',
        'DataCuration',
        'DataCurationResults',
        'DataCurationDocuments',
        'Conversation',
        'Messages',
        'Samples',
        'Charts',
        'Calculations',
        'DbExplorerMemory',
        'DatabaseConfigs',
        'DatabaseTables',
        'DatabaseSchema',
        'KBItems',
        'DeletedKBItems',
        'TableContent',
        'Sources',
        'Jobs',
        'License',
        'Users',
        'Roles',
        'RoleComposites',
        'PinnedConversations',
        'RecentConversations',
        'CurationVersions',
        'Feedback',
        'ImagingSources',
        'Studies',
        'MedicalImaging',
        'DeIDResults',
        'Profiles',
        'PatientRegistryProjects',
        'PatientRegistryReportingSources',
        'CaseReview',
        'Datasets',
        'DatasetItems',
        'DatasetShares',
        'ClinicalTrialJobs',
        'ClinicalTrialCombinedPatients',
        'CpgRag',
        'McpDocs',
        'SessionDocuments',
        'Templates',
        'RegistryStandards',
        'ApiTokens',
        'OntologyBuilderJobs',
    ],
    endpoints: builder => ({
        getRawDocumentContent: builder.query<RawDocumentContent | null, { documentId: string }>({
            query: ({ documentId }) => `/documents/${documentId}/content`,
        }),
        getPatientIds: builder.query<{ patientIds: string[]; total: number }, { search?: string; limit?: number }>({
            query: ({ search, limit }) => {
                const params = new URLSearchParams();
                if (search) params.append('search', search);
                if (limit) params.append('limit', limit.toString());
                return `/documents/patient-ids?${params.toString()}`;
            },
            providesTags: ['Documents'],
        }),
        getNoteTypes: builder.query<{ noteTypes: string[]; total: number }, { search?: string; limit?: number }>({
            query: ({ search, limit }) => {
                const params = new URLSearchParams();
                if (search) params.append('search', search);
                if (limit) params.append('limit', limit.toString());
                return `/documents/note-types?${params.toString()}`;
            },
            providesTags: ['Documents'],
        }),
        getEntityTexts: builder.query<{ entityTexts: string[]; total: number }, { search?: string; limit?: number }>({
            query: ({ search, limit }) => {
                const params = new URLSearchParams();
                if (search) params.append('search', search);
                if (limit) params.append('limit', limit.toString());
                return `/documents/entity-texts?${params.toString()}`;
            },
            providesTags: ['Documents'],
        }),
        getOmopConcepts: builder.query<
            { concepts: Array<{ name: string; code?: string; class?: string }>; total: number },
            { search?: string; limit?: number }
        >({
            query: ({ search, limit }) => {
                const params = new URLSearchParams();
                if (search) params.append('search', search);
                if (limit) params.append('limit', limit.toString());
                return `/documents/omop-concepts?${params.toString()}`;
            },
            providesTags: ['Documents'],
        }),
        getOmopDomains: builder.query<{ domains: string[]; total: number }, { search?: string; limit?: number }>({
            query: ({ search, limit }) => {
                const params = new URLSearchParams();
                if (search) params.append('search', search);
                if (limit) params.append('limit', limit.toString());
                return `/documents/omop-domains?${params.toString()}`;
            },
            providesTags: ['Documents'],
        }),
        getOmopVocabularies: builder.query<
            { vocabularies: string[]; total: number },
            { search?: string; limit?: number }
        >({
            query: ({ search, limit }) => {
                const params = new URLSearchParams();
                if (search) params.append('search', search);
                if (limit) params.append('limit', limit.toString());
                return `/documents/omop-vocabularies?${params.toString()}`;
            },
            providesTags: ['Documents'],
        }),
        getOmopConceptClasses: builder.query<
            { conceptClasses: string[]; total: number },
            { search?: string; limit?: number }
        >({
            query: ({ search, limit }) => {
                const params = new URLSearchParams();
                if (search) params.append('search', search);
                if (limit) params.append('limit', limit.toString());
                return `/documents/omop-concept-classes?${params.toString()}`;
            },
            providesTags: ['Documents'],
        }),
        getSections: builder.query<{ sections: string[]; total: number }, { search?: string; limit?: number }>({
            query: ({ search, limit }) => {
                const params = new URLSearchParams();
                if (search) params.append('search', search);
                if (limit) params.append('limit', limit.toString());
                return {
                    url: `/documents/sections?${params.toString()}`,
                    method: 'GET',
                };
            },
            providesTags: ['Documents'],
        }),
        getAssertions: builder.query<{ assertions: string[]; total: number }, { search?: string; limit?: number }>({
            query: ({ search, limit }) => {
                const params = new URLSearchParams();
                if (search) params.append('search', search);
                if (limit) params.append('limit', limit.toString());
                return `/documents/assertions?${params.toString()}`;
            },
            providesTags: ['Documents'],
        }),
        getEntities: builder.query<{ entities: string[]; total: number }, { search?: string; limit?: number }>({
            query: ({ search, limit }) => {
                const params = new URLSearchParams();
                if (search) params.append('search', search);
                if (limit) params.append('limit', limit.toString());
                return `/documents/entities?${params.toString()}`;
            },
            providesTags: ['Documents'],
        }),
        getMcpDocs: builder.query<{ id: string; name: string; path: string }[], void>({
            query: () => '/mcp-docs',
            providesTags: ['McpDocs'],
        }),
        getMcpDocContent: builder.query<{ id: string; name: string; path: string; content: string }, string>({
            query: path => `/mcp-docs/content?path=${encodeURIComponent(path)}`,
            providesTags: (result, error, path) => [{ type: 'McpDocs', id: path }],
        }),
        // Enriched documents (raw_nlp_annotations_enriched)
        getEnrichedDocuments: builder.query<
            {
                documents: Array<{
                    document_id: string;
                    document_type?: string;
                    patient_id?: string;
                    note_date?: string | null;
                }>;
                total: number;
            },
            { limit?: number; search?: string }
        >({
            query: ({ limit, search }) => {
                const params = new URLSearchParams();
                if (limit != null) params.append('limit', limit.toString());
                if (search) params.append('search', search);
                return `/documents/enriched/documents?${params.toString()}`;
            },
            providesTags: ['Documents'],
        }),
        getEnrichedDocumentsGroupedByPatient: builder.query<
            {
                patients: Array<{
                    patient_id: string;
                    patient_name: string;
                    documents: Array<{
                        document_id: string;
                        document_type?: string;
                        patient_id?: string;
                        note_date?: string | null;
                    }>;
                }>;
                totalDocuments: number;
            },
            { limit?: number; search?: string }
        >({
            query: ({ limit, search }) => {
                const params = new URLSearchParams();
                if (limit != null) params.append('limit', limit.toString());
                if (search) params.append('search', search);
                return `/documents/enriched/documents-by-patient?${params.toString()}`;
            },
            providesTags: ['Documents'],
        }),
        getEnrichedPatientsSummary: builder.query<
            {
                patients: Array<{ patient_id: string; patient_name: string; document_count: number }>;
                totalDocuments: number;
            },
            { limit?: number; search?: string }
        >({
            query: ({ limit, search }) => {
                const params = new URLSearchParams();
                if (limit != null) params.append('limit', limit.toString());
                if (search) params.append('search', search);
                return `/documents/enriched/patients-summary?${params.toString()}`;
            },
            providesTags: ['Documents'],
        }),
        getEnrichedDocumentsForPatient: builder.query<
            {
                documents: Array<{
                    document_id: string;
                    document_type?: string;
                    patient_id?: string;
                    note_date?: string | null;
                }>;
            },
            { patientId: string; search?: string }
        >({
            query: ({ patientId, search }) => {
                const params = new URLSearchParams();
                if (search) params.append('search', search);
                return `/documents/enriched/patients/${encodeURIComponent(patientId)}/documents?${params.toString()}`;
            },
            providesTags: (result, error, { patientId }) => [
                { type: 'Documents', id: `enriched-patient-${patientId}` },
            ],
        }),
        getDocumentEnrichedAnnotations: builder.query<
            Array<{
                ner_chunk: string;
                entity: string;
                start: number;
                end: number;
                sentence?: string;
                concept_name?: string;
                concept_code?: string;
                domain_name?: string;
                vocabulary_id?: string;
                resolution_confidence?: number;
                chunk_id?: string;
                row_id?: string;
                [key: string]: unknown;
            }>,
            { documentId: string }
        >({
            query: ({ documentId }) => `/documents/enriched/${encodeURIComponent(documentId)}/annotations`,
            providesTags: (result, error, { documentId }) => [{ type: 'Documents', id: `enriched-${documentId}` }],
        }),
    }),
});

export const {
    useGetRawDocumentContentQuery,
    useLazyGetRawDocumentContentQuery,
    useGetPatientIdsQuery,
    useGetNoteTypesQuery,
    useGetEntityTextsQuery,
    useGetOmopConceptsQuery,
    useGetOmopDomainsQuery,
    useGetOmopVocabulariesQuery,
    useGetOmopConceptClassesQuery,
    useGetSectionsQuery,
    useGetAssertionsQuery,
    useGetEntitiesQuery,
    useGetMcpDocsQuery,
    useGetMcpDocContentQuery,
    useGetEnrichedDocumentsQuery,
    useLazyGetEnrichedDocumentsQuery,
    useGetEnrichedDocumentsGroupedByPatientQuery,
    useGetEnrichedPatientsSummaryQuery,
    useLazyGetEnrichedDocumentsForPatientQuery,
    useGetDocumentEnrichedAnnotationsQuery,
    useLazyGetDocumentEnrichedAnnotationsQuery,
} = api;
